/* Kalakar India — Collection card Splide init + ATC confetti.
   Performance model:
     - Carousels are LAZY-MOUNTED via IntersectionObserver: a card's Splide only
       initializes when it nears the viewport. Below-fold cards stay as a plain
       first-image (pre-init CSS shows slide 1; native lazy-loading defers the rest).
       This caps upfront image fetches to ~visible cards and avoids mounting 36
       Splide instances at once (lower Total Blocking Time).
     - Within a mounted card, Splide lazyLoad 'nearby' + preloadPages 0 keeps
       slides 2-5 (data-splide-lazy) from fetching until swiped.
   Contexts:
     1. Collection grid — observed on DOMContentLoaded
     2. PDP recommendations / related — AJAX-injected; caught by MutationObserver, then observed
     3. Cards inside Dawn's <slider-component> carousels — skipped (CSS shows first image)
   Confetti library loads on the first ATC tap, not on every page. */
(function () {
  var splideInstances = [];
  var firstCardMounted = false;
  var io = null;
  var DOT_WINDOW = 3;

  /* Limit the pagination to a sliding window of DOT_WINDOW dots.
     Slide indices outside the window are hidden, so the pill shows at most
     3 dots while swipe still moves through every image. The active dot stays
     within the window — centred except at the two ends. */
  function applyDotWindow(splide) {
    try {
      var pagination = splide.root.querySelector('.splide__pagination');
      if (!pagination) return;
      var dots = pagination.querySelectorAll('.splide__pagination__page');
      var total = dots.length;
      if (!total) return;
      if (total <= DOT_WINDOW) {
        dots.forEach(function (dot) {
          var li = dot.closest('li') || dot;
          li.style.display = '';
        });
        return;
      }
      var active = splide.index;
      var start = Math.min(Math.max(active - 1, 0), total - DOT_WINDOW);
      dots.forEach(function (dot, i) {
        var li = dot.closest('li') || dot;
        li.style.display = (i >= start && i < start + DOT_WINDOW) ? '' : 'none';
      });
    } catch (e) {}
  }

  function mountCard(el) {
    if (typeof Splide === 'undefined') return;
    if (el.classList.contains('is-initialized')) return;
    if (el.closest('slider-component')) return;

    try {
      var splide = new Splide(el, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        speed: 350,
        arrows: false,
        pagination: true,
        drag: true,
        flickMaxPages: 1,
        rewind: false,
        keyboard: false,
        updateOnMove: true,
        lazyLoad: 'nearby',
        preloadPages: 0,
      });

      /* Plain 3-dot sliding window — cap visible dots, slide as you pass image 3.
         Re-apply on the next frame too, so it survives Splide's pagination
         rebuild during the first-card nudge (options swap → dots re-render). */
      var reWindow = function () {
        applyDotWindow(splide);
        if (window.requestAnimationFrame) {
          requestAnimationFrame(function () { applyDotWindow(splide); });
        }
      };
      splide.on('mounted moved updated refresh pagination:mounted pagination:updated', reWindow);

      splide.mount();

      splideInstances.push(splide);

      /* Swipe-hint nudge — only the first card of the grid, once. */
      if (el.hasAttribute('data-card-splide-first') && !firstCardMounted) {
        firstCardMounted = true;
        var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReduced) {
          setTimeout(function () {
            try {
              var nudgeSpeed = splide.options.speed;   // remember snappy swipe speed
              splide.options = { speed: 900 };          // slow, gentle glide for the hint only
              splide.go(1);
              setTimeout(function () {
                try {
                  splide.go(0);
                  setTimeout(function () {
                    try { splide.options = { speed: nudgeSpeed }; } catch (e) {}  // restore snappy swipes
                  }, 1000);
                } catch (e) {}
              }, 1100);
            } catch (e) {}
          }, 900);
        }
      }
    } catch (err) {
      /* mount failed — card falls back to CSS-only first-image state */
    }
  }

  function observeCards(root) {
    root = root || document;
    var els = root.querySelectorAll('.kk-card-splide[data-card-splide]:not(.is-initialized)');
    if (!els.length) return;

    els.forEach(function (el) {
      if (el.closest('slider-component')) return;
      if (io) {
        io.observe(el);
      } else {
        mountCard(el); /* no IntersectionObserver support — mount immediately */
      }
    });
  }

  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          mountCard(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '250px 0px' }); /* mount ~250px before entering viewport for a smooth scroll */
  }

  function refreshAll() {
    splideInstances.forEach(function (s) {
      try { s.refresh(); } catch (e) {}
    });
  }

  /* Observe on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeCards(); });
  } else {
    observeCards();
  }

  /* Refresh mounted instances after window.load (images/fonts finalized). */
  window.addEventListener('load', function () {
    setTimeout(refreshAll, 100);
  });

  /* Re-observe after Shopify section AJAX swaps. */
  document.addEventListener('shopify:section:load', function () { observeCards(); });

  /* MutationObserver: catch AJAX-injected cards (product-recommendations, etc), then observe them. */
  if ('MutationObserver' in window) {
    var pending = null;
    var domObserver = new MutationObserver(function (mutations) {
      var foundNew = false;
      for (var i = 0; i < mutations.length && !foundNew; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length && !foundNew; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('.kk-card-splide[data-card-splide]:not(.is-initialized)')) ||
            (node.querySelector && node.querySelector('.kk-card-splide[data-card-splide]:not(.is-initialized)'))
          ) {
            foundNew = true;
          }
        }
      }
      if (foundNew) {
        if (pending) clearTimeout(pending);
        pending = setTimeout(function () {
          pending = null;
          observeCards();
        }, 50);
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Confetti on ATC overlay clicks — library loaded on first tap only ── */
  var confettiState = { loading: false, queue: [] };
  var CONFETTI_SRC = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js';

  function ensureConfetti(cb) {
    if (typeof confetti !== 'undefined') { cb(); return; }
    confettiState.queue.push(cb);
    if (confettiState.loading) return;
    confettiState.loading = true;
    var s = document.createElement('script');
    s.src = CONFETTI_SRC;
    s.async = true;
    s.onload = function () {
      confettiState.queue.forEach(function (fn) { fn(); });
      confettiState.queue = [];
    };
    s.onerror = function () {
      confettiState.loading = false;
      confettiState.queue = [];
    };
    document.head.appendChild(s);
  }

  function fireCardConfetti() {
    if (typeof confetti === 'undefined') return;
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x: 0.5, y: 1 },
      colors: ['#bc5631', '#f5e6d8', '#e8a838', '#5c3a1f', '#ffffff'],
      startVelocity: 35,
      gravity: 0.8,
      ticks: 200,
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.kk-card-atc');
    if (!btn) return;
    if (btn.closest('modal-opener')) return; /* skip "Choose options" multi-variant button */
    var tapTime = Date.now();
    ensureConfetti(function () {
      var remaining = Math.max(0, 800 - (Date.now() - tapTime));
      setTimeout(fireCardConfetti, remaining);
    });
  });
})();